import { LocaleData, LocaleConfig, LocaleModule } from './types';

export * from './types';

// ---------------------------------------------------------------------------
// Lazy locale loading
//
// On startup we only load the ONE locale the user actually needs.
// All 83 locale files are registered lazily — they are NOT downloaded until
// requested.  The language picker can call loadLocale() on demand.
// ---------------------------------------------------------------------------

/** Lazy glob — no `eager: true` so Vite does NOT bundle all 83 files upfront. */
const lazyModules = import.meta.glob<LocaleModule>([
    './**/*.ts',
    '!./index.ts',
    '!./types.ts'
]);

/** Config-only objects extracted at build time (tiny — just metadata, no content). */
const eagerMeta = import.meta.glob<LocaleModule>([
    './**/*.ts',
    '!./index.ts',
    '!./types.ts'
], { eager: true, import: 'config' });

/** In-memory cache so each locale is fetched at most once. */
export const translations: Record<string, LocaleData> = {};

/** Filled synchronously from the config-only eager import (no LocaleData payload). */
export const supportedLocalesConfig: LocaleConfig[] = [];

// Build the config list from the thin eager imports (config metadata only, no strings)
for (const path in eagerMeta) {
    const cfg = eagerMeta[path] as unknown as LocaleConfig;
    if (cfg && cfg.id) {
        supportedLocalesConfig.push(cfg);
    }
}

/**
 * Load and cache a locale by ID.  Returns the LocaleData, or null on failure.
 * Safe to call multiple times — cached after first fetch.
 */
export async function loadLocale(id: string): Promise<LocaleData | null> {
    if (translations[id]) return translations[id];

    // Find the matching module path
    const entry = Object.entries(lazyModules).find(([path]) => {
        // paths look like "./Americas/en-us.ts" — match by config id
        return path.toLowerCase().includes(`/${id.toLowerCase()}.ts`);
    });

    if (!entry) {
        console.warn(`[Locales] No module found for locale: ${id}`);
        return null;
    }

    try {
        const mod = await entry[1]();
        if (mod?.default) {
            translations[id] = mod.default;
            return mod.default;
        }
    } catch (e) {
        console.error(`[Locales] Failed to load locale: ${id}`, e);
    }
    return null;
}

/**
 * Detects the best possible locale based on browser settings.
 * Falls back to 'en-US' if no suitable match is found.
 */
export function getBestLocale(): string {
    const userLanguages = navigator.languages || [navigator.language];
    const supportedIds = supportedLocalesConfig.map(c => c.id);

    for (const lang of userLanguages) {
        if (supportedIds.includes(lang)) return lang;
        const langCode = lang.split('-')[0].toLowerCase();
        const partialMatch = supportedIds.find(id => id.split('-')[0].toLowerCase() === langCode);
        if (partialMatch) return partialMatch;
    }
    return 'en';
}
